/**
 * 认证状态管理Store
 * 负责用户登录、登出、资料管理等认证相关功能
 *
 * @module stores/simpleAuthStore
 */

import { defineStore } from "pinia";
import { ref, computed, type Ref, type ComputedRef } from "vue";
import {
  authService,
  type LoginRequest as WorkbenchLoginRequest,
  type UpdateProfileRequest,
  type WorkbenchCredentials,
} from "../../src/services/authService";
import { useCapabilityStore } from "./capabilityStore";

// ==================== 类型定义 ====================

/**
 * 用户信息接口
 */
export interface User {
  id: string;
  phone_number: string;
  nickname?: string;
  email?: string;
  avatar_url?: string;
  gender?: string;
  birth_date?: string;
  height_cm?: number;
  weight_kg?: number;
  bio?: string;
  is_active?: boolean;
  status?: string;
  roles?: string[];
  role?: string;
  tenant_id?: string | null;
  department_id?: string | null;
  department_ids?: string[];
  doctor_profile_id?: string | null;
  totp_enabled?: boolean;
  registration_timestamp?: string;
  last_login_timestamp?: string;
  created_at?: string;
  updated_at?: string;
}

interface LogoutOptions {
  suppressMessage?: boolean;
  localOnly?: boolean;
  skipBroadcast?: boolean;
}

// 导出UpdateProfileRequest以便其他地方使用
export type { UpdateProfileRequest };

/** 全局唯一访问令牌读取入口；业务代码不得直接读取 sessionStorage。 */
export function getAccessToken(): string {
  try {
    return sessionStorage.getItem("access_token") || "";
  } catch {
    return "";
  }
}

// ==================== Store定义 ====================

export const useSimpleAuthStore = defineStore("simpleAuth", () => {
  const AUTH_SYNC_KEY = "pgy_auth_sync_event";
  const tabId = `tab_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  // 跨标签页刷新锁：refresh token 轮换后旧 token 立即拉黑，多标签页并发刷新时
  // 后到者必然失败并触发 logout 广播；用短锁让只有一个标签页发起刷新。
  const REFRESH_LOCK_KEY = "pgy_refresh_lock";
  const REFRESH_LOCK_TTL_MS = 10_000;
  const REFRESH_WAIT_TIMEOUT_MS = 5_000;
  const REFRESH_WAIT_INTERVAL_MS = 200;

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms));

  const tryAcquireRefreshLock = (): boolean => {
    try {
      const now = Date.now();
      const raw = localStorage.getItem(REFRESH_LOCK_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { owner?: string; at?: number };
        if (
          parsed?.owner &&
          parsed.owner !== tabId &&
          now - Number(parsed.at || 0) < REFRESH_LOCK_TTL_MS
        ) {
          return false;
        }
      }
      localStorage.setItem(
        REFRESH_LOCK_KEY,
        JSON.stringify({ owner: tabId, at: now }),
      );
      return true;
    } catch {
      // 存储不可用（隐私模式等）：回退到无锁行为
      return true;
    }
  };

  const releaseRefreshLock = (): void => {
    try {
      const raw = localStorage.getItem(REFRESH_LOCK_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { owner?: string };
      if (parsed?.owner === tabId) {
        localStorage.removeItem(REFRESH_LOCK_KEY);
      }
    } catch {
      // 忽略存储异常
    }
  };
  const authChannel =
    typeof BroadcastChannel !== "undefined"
      ? new BroadcastChannel("pgy_auth_channel")
      : null;

  // ==================== State ====================

  /**
   * 当前用户信息
   */
  const user: Ref<User | null> = ref(null);

  /**
   * 访问令牌
   */
  const token: Ref<string | null> = ref(getAccessToken() || null);

  /**
   * 全局加载状态
   */
  const isLoading: Ref<boolean> = ref(false);

  /**
   * 验证码是否已发送
   */
  const isCodeSent: Ref<boolean> = ref(false);

  /**
   * 重发验证码倒计时（秒）
   */
  const resendCountdown: Ref<number> = ref(0);

  /**
   * 错误消息
   */
  const errorMessage: Ref<string> = ref("");

  /**
   * 成功消息
   */
  const successMessage: Ref<string> = ref("");

  /**
   * 倒计时定时器引用（防止内存泄漏）
   */
  let countdownTimer: number | null = null;
  let initPromise: Promise<void> | null = null;
  let refreshPromise: Promise<boolean> | null = null;
  let fetchUserInfoPromise: Promise<{
    success: boolean;
    message?: string;
    user?: User;
  }> | null = null;
  let syncInitialized = false;

  // ==================== Getters ====================

  /**
   * 是否已登录
   */
  const isLoggedIn: ComputedRef<boolean> = computed(() => {
    return !!token.value && !!user.value;
  });

  /**
   * 获取用户信息对象
   */
  const userInfo: ComputedRef<User | null> = computed(() => user.value);

  /**
   * 获取用户昵称
   */
  const userNickname: ComputedRef<string> = computed(() => {
    return (
      user.value?.nickname || `用户${user.value?.phone_number?.slice(-4) || ""}`
    );
  });

  /**
   * 检查是否为新用户
   */
  const isNewUser: ComputedRef<boolean> = computed(() => {
    return !user.value?.nickname || !user.value?.email;
  });

  /**
   * 获取用户头像URL
   */
  const userAvatar: ComputedRef<string> = computed(() => {
    return user.value?.avatar_url || "";
  });

  /**
   * 检查token是否存在
   */
  const hasToken: ComputedRef<boolean> = computed(() => {
    return !!token.value;
  });

  /**
   * 获取用户ID
   */
  const userId: ComputedRef<string> = computed(() => {
    return user.value?.id || "";
  });

  /**
   * 获取用户手机号
   */
  const userPhone: ComputedRef<string> = computed(() => {
    return user.value?.phone_number || "";
  });

  // ==================== Actions ====================

  /**
   * 设置加载状态
   */
  const setLoading = (loading: boolean): void => {
    isLoading.value = loading;
  };

  /**
   * 设置错误消息（5秒后自动清除）
   */
  const setError = (message: string): void => {
    errorMessage.value = message;
    successMessage.value = "";

    setTimeout(() => {
      if (errorMessage.value === message) {
        errorMessage.value = "";
      }
    }, 5000);
  };

  /**
   * 设置成功消息（5秒后自动清除）
   */
  const setSuccess = (message: string): void => {
    successMessage.value = message;
    errorMessage.value = "";

    setTimeout(() => {
      if (successMessage.value === message) {
        successMessage.value = "";
      }
    }, 5000);
  };

  /**
   * 清除所有消息
   */
  const clearMessages = (): void => {
    errorMessage.value = "";
    successMessage.value = "";
  };

  const setToken = (accessToken: string | null): void => {
    token.value = accessToken;
    if (accessToken) {
      sessionStorage.setItem("access_token", accessToken);
    } else {
      sessionStorage.removeItem("access_token");
    }
  };

  /**
   * 等保三级：统一向 store 写入登录会话，禁止外部直接 localStorage 暴露 Token。
   * 注册/登录页面写完后调用此方法即可。
   */
  const hydrateFromLoginResponse = (payload: {
    access_token?: string | null;
    user?: unknown;
  }): void => {
    if (!payload) return;
    if (
      typeof payload.access_token === "string" ||
      payload.access_token === null
    ) {
      setToken(payload.access_token ?? null);
    }
    if (payload.user) {
      syncIdentity(payload.user as User);
      user.value = payload.user as any;
    }
  };

  const clearSessionState = (): void => {
    user.value = null;
    setToken(null);
    isCodeSent.value = false;
    resendCountdown.value = 0;
    clearMessages();

    if (countdownTimer) {
      clearInterval(countdownTimer);
      countdownTimer = null;
    }
  };

  /** 账号/租户/科室指纹：任一变化都意味着权益缓存必须失效并重新加载 */
  const identityFingerprint = (candidate: User | null): string =>
    candidate
      ? `${candidate.id ?? ""}|${candidate.tenant_id ?? ""}|${candidate.department_id ?? ""}`
      : "";

  /**
   * 身份变化时同步作废权益缓存（同步执行，确保过渡期间旧请求的迟到响应
   * 不会把旧权限写回；随后由登录页/临床首页重新 load 新身份权益）。
   */
  const syncIdentity = (candidate: User | null): void => {
    if (identityFingerprint(candidate) === identityFingerprint(user.value)) {
      return;
    }
    try {
      useCapabilityStore().invalidate();
    } catch (error) {
      console.warn("⚠️ 权益缓存失效失败:", error);
    }
  };

  /** 清除所有与账号、租户和工作科室绑定的业务状态。 */
  const clearBusinessSessionState = async (): Promise<void> => {
    const resetters = [
      async () => {
        // 权益与账号/机构/科室绑定：先作废，旧请求的迟到响应不得恢复旧权限
        useCapabilityStore().invalidate();
      },
      async () => {
        const { usePatientManagementStore } = await import(
          "@patient-management/patient/infrastructure/patientStore"
        );
        const store = usePatientManagementStore();
        store.clearLastManagedPatient();
        store.resetAll();
      },
      async () => {
        const { useMedicalStore } = await import(
          "@clinical-record/infrastructure/stores/recordStore"
        );
        useMedicalStore().resetAll();
      },
      async () => {
        // 两个 surface 的对话框状态都要清理：chat（经典布局）+ workspace-chat（work 模式）
        const { useChatStore, useWorkspaceChatStore } = await import(
          "@clinical-record/infrastructure/stores/chatStore"
        );
        useChatStore().$reset();
        useWorkspaceChatStore().$reset();
      },
      async () => {
        const { useQueueStore } = await import(
          "@patient-management/queue/domain/queueStore"
        );
        useQueueStore().resetAll();
      },
      async () => {
        const { useTemporalAnalysisStore } = await import(
          "@ai-medicine/temporal-analysis/domain/temporalAnalysisStore"
        );
        useTemporalAnalysisStore().$reset();
      },
    ];
    const results = await Promise.allSettled(resetters.map((reset) => reset()));
    results.forEach((result) => {
      if (result.status === "rejected") {
        console.warn("⚠️ 业务会话状态清理失败:", result.reason);
      }
    });
    // 即使患者 Store 尚未成功加载，也要保证持久化选择被清除。
    try {
      localStorage.removeItem("pm_lastManagedPatient");
    } catch {
      // 浏览器禁用存储时无需阻断登出。
    }
  };

  const redirectToLoginPage = (): void => {
    if (typeof window === "undefined") return;
    if (window.location.pathname.includes("/login")) return;
    window.location.href = "/login";
  };

  /** 跨标签页会话同步事件（结构由 broadcastSessionEvent 约定） */
  interface SessionSyncEvent {
    type?: string;
    sourceId?: string;
    accessToken?: string;
    userId?: string;
    [key: string]: unknown;
  }

  const applyRemoteSessionEvent = (
    payload: SessionSyncEvent | null | undefined,
  ): void => {
    if (!payload || payload.sourceId === tabId) return;

    if (payload.type === "logout") {
      void clearBusinessSessionState();
      clearSessionState();
      redirectToLoginPage();
      return;
    }

    if (
      payload.type === "token-refreshed" &&
      typeof payload.accessToken === "string"
    ) {
      setToken(payload.accessToken);
    }

    if (payload.type === "new_login") {
      // 当前标签页已登录且新登录用户与当前用户不同，发出警告
      if (user.value && payload.userId && payload.userId !== user.value.id) {
        console.warn(
          "⚠️ 检测到同浏览器其他标签页登录了不同账号（userId: %s），当前标签页会话不受影响（userId: %s）",
          payload.userId,
          user.value.id,
        );
      }
    }
  };

  const broadcastSessionEvent = (payload: Record<string, unknown>): void => {
    const eventPayload = {
      ...payload,
      sourceId: tabId,
      timestamp: Date.now(),
    };

    try {
      localStorage.setItem(AUTH_SYNC_KEY, JSON.stringify(eventPayload));
    } catch (error) {
      console.warn("⚠️ 写入认证同步事件失败:", error);
    }

    authChannel?.postMessage(eventPayload);
  };

  const setupSessionSync = (): void => {
    if (syncInitialized || typeof window === "undefined") {
      return;
    }

    window.addEventListener("storage", (event) => {
      if (event.key !== AUTH_SYNC_KEY || !event.newValue) return;
      try {
        applyRemoteSessionEvent(JSON.parse(event.newValue));
      } catch (error) {
        console.warn("⚠️ 解析认证同步事件失败:", error);
      }
    });

    authChannel?.addEventListener("message", (event) => {
      applyRemoteSessionEvent(event.data);
    });

    syncInitialized = true;
  };

  /** 错误形态：兼容 fetch/axios/自定义错误对象 */
  type ErrorLike =
    | { name?: string; message?: string; status?: number }
    | null
    | undefined;

  const isNetworkLikeError = (error: ErrorLike): boolean => {
    return (
      error?.name === "NetworkError" ||
      Boolean(error?.message && error.message.includes("无法连接到服务器"))
    );
  };

  const isAuthLikeError = (error: ErrorLike): boolean => {
    return (
      error?.status === 401 ||
      error?.status === 403 ||
      error?.name === "AuthError"
    );
  };

  const shouldKeepSession = (error: ErrorLike): boolean => {
    return (
      isNetworkLikeError(error) ||
      (typeof error?.status === "number" && error.status >= 500)
    );
  };

  /**
   * 发送验证码（用户名 + 密码 + 可选单位编码）
   * @param credentials - 工作台登录凭据
   */
  const sendVerificationCode = async (
    credentials: WorkbenchCredentials,
  ): Promise<{
    success: boolean;
    message?: string;
    debugCode?: string;
    waitSeconds?: number;
    errorCode?: string;
  }> => {
    setLoading(true);
    clearMessages();

    try {
      const data = await authService.sendVerificationCode(credentials);

      if (data.success) {
        isCodeSent.value = true;
        setSuccess(data.message);
        startResendCountdown(data.wait_seconds);
        return {
          success: true,
          debugCode: data.debug_code,
          waitSeconds: data.wait_seconds,
        };
      } else {
        setError(data.message);
        return { success: false, message: data.message };
      }
    } catch (error: unknown) {
      const err = error as { message?: string; errorCode?: string };
      const message = err.message || "网络错误，请检查服务器连接";
      const errorCode: string | undefined = err?.errorCode;
      setError(message);
      return { success: false, message, errorCode };
    } finally {
      setLoading(false);
    }
  };

  /**
   * 验证码登录（用户名 + 密码 + 验证码 + 可选单位编码）
   * @param credentials - 工作台登录凭据（含验证码）
   */
  const loginWithCode = async (
    credentials: WorkbenchLoginRequest,
  ): Promise<{
    success: boolean;
    message?: string;
    user?: User;
    isNewUser?: boolean;
    errorCode?: string;
  }> => {
    setLoading(true);
    clearMessages();

    try {
      const data = await authService.loginWithCode(credentials);

      if (data.success) {
        // 保存认证信息
        setToken(data.access_token);
        syncIdentity(data.user ?? null);
        user.value = data.user;
        broadcastSessionEvent({
          type: "token-refreshed",
          accessToken: data.access_token,
        });
        broadcastSessionEvent({
          type: "new_login",
          userId: data.user?.id || "",
        });

        setSuccess(
          `${data.message}${data.is_new_user ? " (新用户)" : " (欢迎回来)"}`,
        );

        // 认证与临床上下文解耦（T17）：登录只建立身份；
        // 恢复患者与临床场景由临床首页（index.vue）负责，教学路由不触发

        return {
          success: true,
          user: data.user,
          isNewUser: data.is_new_user,
        };
      } else {
        setError(data.message);
        return { success: false, message: data.message };
      }
    } catch (error: unknown) {
      const err = error as { message?: string; errorCode?: string };
      const message = err.message || "网络错误，请检查服务器连接";
      const errorCode: string | undefined = err?.errorCode;
      setError(message);
      return { success: false, message, errorCode };
    } finally {
      setLoading(false);
    }
  };

  /**
   * 获取当前用户信息
   */
  const performFetchUserInfo = async (): Promise<{
    success: boolean;
    message?: string;
    user?: User;
  }> => {
    if (!token.value) {
      return { success: false, message: "未登录" };
    }

    try {
      const data = await authService.fetchUserInfo(token.value);

      if (data.success) {
        const nextUser = data.data?.user || data.user || null;
        syncIdentity(nextUser);
        user.value = nextUser;
        return { success: true, user: user.value || undefined };
      } else {
        await logout({ suppressMessage: true });
        return { success: false, message: data.message };
      }
    } catch (error: unknown) {
      const err = error as { message?: string; status?: number; name?: string };
      if (isAuthLikeError(err)) {
        const refreshed = await refreshSession();
        if (refreshed && token.value) {
          try {
            const retried = await authService.fetchUserInfo(token.value);
            if (retried.success) {
              const nextUser = retried.data?.user || retried.user || null;
              syncIdentity(nextUser);
              user.value = nextUser;
              return { success: true, user: user.value || undefined };
            }
          } catch (retryError: unknown) {
            const retryErr = retryError as {
              message?: string;
              status?: number;
              name?: string;
            };
            if (shouldKeepSession(retryErr)) {
              return {
                success: false,
                message:
                  retryErr.message ||
                  "无法连接到服务器，请检查后端服务是否运行",
              };
            }
          }
        }
        await logout({ suppressMessage: true });
        return { success: false, message: err.message || "登录状态已失效" };
      }

      if (shouldKeepSession(err)) {
        console.warn("⚠️ 网络连接失败，但保持登录状态:", err.message);
        return {
          success: false,
          message: err.message || "无法连接到服务器，请检查后端服务是否运行",
        };
      }

      console.error("❌ 获取用户信息失败，退出登录:", error);
      await logout({ suppressMessage: true });
      return { success: false, message: err.message || "获取用户信息失败" };
    }
  };

  const fetchUserInfo = (): Promise<{
    success: boolean;
    message?: string;
    user?: User;
  }> => {
    if (fetchUserInfoPromise) return fetchUserInfoPromise;

    fetchUserInfoPromise = performFetchUserInfo().finally(() => {
      fetchUserInfoPromise = null;
    });
    return fetchUserInfoPromise;
  };

  const refreshSession = async (): Promise<boolean> => {
    if (refreshPromise) {
      return refreshPromise;
    }

    refreshPromise = (async () => {
      const beforeToken = getAccessToken();
      let lockHeld = false;
      try {
        lockHeld = tryAcquireRefreshLock();
        if (!lockHeld) {
          // 其他标签页正在刷新：等待其广播的新 token，或等锁释放后自己刷新
          const deadline = Date.now() + REFRESH_WAIT_TIMEOUT_MS;
          while (Date.now() < deadline) {
            await sleep(REFRESH_WAIT_INTERVAL_MS);
            const current = getAccessToken();
            if (current && current !== beforeToken) {
              return true;
            }
            lockHeld = tryAcquireRefreshLock();
            if (lockHeld) break;
          }
          if (!lockHeld) {
            console.warn("⚠️ 等待其他标签页刷新超时");
            return false;
          }
        }

        const data = await authService.refreshToken();
        if (!data.success || !data.access_token) {
          return false;
        }

        setToken(data.access_token);
        broadcastSessionEvent({
          type: "token-refreshed",
          accessToken: data.access_token,
        });
        return true;
      } catch (error) {
        console.warn("⚠️ 静默续期失败:", error);
        return false;
      } finally {
        if (lockHeld) releaseRefreshLock();
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  };

  /**
   * 更新用户资料
   * @param profileData - 要更新的资料
   */
  const updateProfile = async (
    profileData: UpdateProfileRequest,
    allowRefresh = true,
  ): Promise<{
    success: boolean;
    message?: string;
    user?: User;
  }> => {
    if (!token.value) {
      setError("请先登录");
      return { success: false, message: "未登录" };
    }

    setLoading(true);
    clearMessages();

    try {
      const data = await authService.updateProfile(token.value, profileData);

      if (data.success) {
        user.value = data.data?.user || data.user || null;
        setSuccess("用户资料更新成功");
        return { success: true, user: user.value || undefined };
      } else {
        setError(data.message || "更新失败");
        return { success: false, message: data.message };
      }
    } catch (error: unknown) {
      const err = error as { message?: string; status?: number; name?: string };
      if (allowRefresh && isAuthLikeError(err)) {
        const refreshed = await refreshSession();
        if (refreshed) {
          return updateProfile(profileData, false);
        }
        await logout({ suppressMessage: true });
      }
      const message = err.message || "网络错误，请重试";
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * 登出
   */
  const logout = async (options: LogoutOptions = {}): Promise<void> => {
    try {
      if (!options.localOnly && token.value) {
        await authService.logout();
      }
    } catch (error) {
      console.warn("⚠️ 登出接口调用失败:", error);
    } finally {
      await clearBusinessSessionState();
      clearSessionState();
      if (!options.skipBroadcast) {
        broadcastSessionEvent({ type: "logout" });
      }
      if (!options.suppressMessage) {
        setSuccess("已成功登出");
      }
    }
  };

  /**
   * 重置验证码状态
   */
  const resetCodeState = (): void => {
    isCodeSent.value = false;
    resendCountdown.value = 0;
    clearMessages();

    if (countdownTimer) {
      clearInterval(countdownTimer);
      countdownTimer = null;
    }
  };

  /**
   * 开始重发倒计时
   * @param seconds - 倒计时秒数；后端给出 wait_seconds 时优先使用，否则默认 60 秒
   */
  const startResendCountdown = (seconds?: number): void => {
    // 清除旧计时器
    if (countdownTimer) {
      clearInterval(countdownTimer);
    }

    const total =
      typeof seconds === "number" && Number.isFinite(seconds) && seconds > 0
        ? Math.floor(seconds)
        : 60;

    resendCountdown.value = total;
    countdownTimer = window.setInterval(() => {
      resendCountdown.value--;
      if (resendCountdown.value <= 0) {
        if (countdownTimer) {
          clearInterval(countdownTimer);
          countdownTimer = null;
        }
        resendCountdown.value = 0;
      }
    }, 1000);
  };

  /**
   * 检查系统健康状态
   */
  const checkSystemHealth = async (): Promise<{
    success: boolean;
    message?: string;
    data?: unknown;
  }> => {
    try {
      const data = await authService.checkHealth();
      return {
        success: data.status === "healthy",
        data: data,
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: (error as Error | undefined)?.message || "无法连接到服务器",
      };
    }
  };

  /**
   * 初始化认证状态（页面加载时调用）
   */
  const initAuth = async (): Promise<void> => {
    if (initPromise) {
      return initPromise;
    }

    initPromise = (async () => {
      // 登录/首次设密等公开页面没有 sessionStorage token 时，不主动 refresh。
      // 否则会在每次进入登录页时产生一次 401 噪声（refresh_token cookie 不存在属于正常状态）。
      if (!token.value && window.location.pathname.startsWith("/login")) {
        return;
      }

      if (token.value) {
        // sessionStorage 中已有 token（刷新页面时保留），直接获取用户信息
        const result = await fetchUserInfo();
        if (!result.success) {
          if (result.message?.includes("无法连接到服务器")) {
            console.warn("⚠️ 初始化认证失败（网络问题），保持登录状态");
          }
        }
        // 认证与临床上下文解耦（T17）：仅恢复身份，
        // 患者与临床场景恢复由临床首页按身份执行，教学路由不产生临床副作用
      } else {
        // 新标签页：sessionStorage 中无 token，尝试通过 refresh_token cookie 恢复会话
        const refreshed = await refreshSession();
        if (refreshed && token.value) {
          await fetchUserInfo();
        }
      }
    })();

    try {
      await initPromise;
    } finally {
      initPromise = null;
    }
  };

  setupSessionSync();

  // ==================== Return ====================

  return {
    // State
    user,
    token,
    isLoading,
    isCodeSent,
    resendCountdown,
    errorMessage,
    successMessage,

    // Getters
    isLoggedIn,
    userInfo,
    userNickname,
    isNewUser,
    userAvatar,
    hasToken,
    userId,
    userPhone,

    // Actions
    setLoading,
    setError,
    setSuccess,
    clearMessages,
    sendVerificationCode,
    loginWithCode,
    fetchUserInfo,
    refreshSession,
    updateProfile,
    logout,
    resetCodeState,
    startResendCountdown,
    checkSystemHealth,
    initAuth,
    hydrateFromLoginResponse,
    clearBusinessSessionState,
  };
});

// ==================== 简化的组合式函数 ====================

/**
 * 简化的认证组合式函数接口
 */
export interface SimpleAuthComposable {
  // 响应式属性
  isLoggedIn: boolean;
  userInfo: User | null;
  userNickname: string;
  userAvatar: string;
  isLoading: boolean;
  hasToken: boolean;

  // 方法
  loginWithCode: (credentials: WorkbenchLoginRequest) => Promise<{
    success: boolean;
    message?: string;
    user?: User;
    isNewUser?: boolean;
  }>;
  sendVerificationCode: (credentials: WorkbenchCredentials) => Promise<{
    success: boolean;
    message?: string;
    debugCode?: string;
    waitSeconds?: number;
  }>;
  logout: () => Promise<void>;
  fetchUserInfo: () => Promise<{
    success: boolean;
    message?: string;
    user?: User;
  }>;
  updateProfile: (profileData: UpdateProfileRequest) => Promise<{
    success: boolean;
    message?: string;
    user?: User;
  }>;
  resetCodeState: () => void;
  checkSystemHealth: () => Promise<{
    success: boolean;
    message?: string;
    data?: unknown;
  }>;
}

/**
 * 简化的认证组合式函数
 * 提供更简单的使用方式
 */
export const useSimpleAuth = (): SimpleAuthComposable => {
  const authStore = useSimpleAuthStore();

  return {
    // 响应式属性
    get isLoggedIn() {
      return authStore.isLoggedIn;
    },
    get userInfo() {
      return authStore.userInfo;
    },
    get userNickname() {
      return authStore.userNickname;
    },
    get userAvatar() {
      return authStore.userAvatar;
    },
    get isLoading() {
      return authStore.isLoading;
    },
    get hasToken() {
      return authStore.hasToken;
    },

    // 方法
    loginWithCode: authStore.loginWithCode,
    sendVerificationCode: authStore.sendVerificationCode,
    logout: authStore.logout,
    fetchUserInfo: authStore.fetchUserInfo,
    updateProfile: authStore.updateProfile,
    resetCodeState: authStore.resetCodeState,
    checkSystemHealth: authStore.checkSystemHealth,
  };
};
